import React, { useEffect, useRef } from 'react';

/**
 * Mouse-reactive flow-field swirl background.
 *
 * A set of long-lived particles drift slowly through the canvas. When
 * the pointer is near, each particle picks up a tangential force around
 * the pointer position, producing the looping curls / "spiral over the
 * hand" effect you see on the Webuzo login. With no mouse movement the
 * field keeps a gentle ambient drift so the canvas never looks frozen.
 *
 * Self-contained: just drop <LoginSwirlBackground /> as the first child
 * of an absolutely-positioned-anchored container. It sizes itself to the
 * parent via ResizeObserver, runs on requestAnimationFrame, and cleans
 * up on unmount.
 */
const LoginSwirlBackground = ({
  // Earth-tone palette tuned to read on a near-black background.
  palette = ['#3a8fb7', '#a35a4a', '#6e8e4f', '#c0843f', '#7e6f9e', '#46a39a'],
  // How transparent each frame's "wipe" is. Lower = longer ribbons.
  trail = 0.06,
  // What colour the trail wipe uses. Should match the wrapper background;
  // override when rendering on a light surface (see e.g. trailColor='#fff').
  trailColor = '11, 19, 36',
  // CSS background for the wrapper. Override for a light surface.
  background = 'radial-gradient(circle at 30% 30%, #15233d 0%, #0b1324 55%, #060c18 100%)',
  // Per-segment alpha. Lower this on light surfaces so ribbons stay subtle.
  segmentAlpha = 0.55,
  // Stroke width for each ribbon segment. Bump up on light surfaces
  // where thin lines tend to wash out against the wipe colour.
  lineWidth = 1.1,
  // How many particles. Each writes a few thousand pixels per frame,
  // so on a typical laptop 90–140 is the sweet spot for fluidity.
  count = 110,
  // How far the swirl reaches.
  swirlRadius = 320,
  // How strongly the swirl pulls particles into a circular orbit.
  swirlStrength = 0.045,
  // Velocity damping each frame. Closer to 1 = particles preserve energy
  // longer and trace longer, more curl-like ribbons (Webuzo-style).
  damping = 0.985,
}) => {
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const rafRef    = useRef(0);
  const particlesRef = useRef([]);
  const mouseRef  = useRef({ x: -9999, y: -9999, active: false });

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap   = wrapperRef.current;
    if (!canvas || !wrap) return;
    const ctx = canvas.getContext('2d');

    let width  = 0;
    let height = 0;
    const DPR  = Math.min(window.devicePixelRatio || 1, 2);

    const seedParticles = () => {
      const list = [];
      for (let i = 0; i < count; i++) {
        list.push({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * 0.3,
          vy: (Math.random() - 0.5) * 0.3,
          color: palette[Math.floor(Math.random() * palette.length)],
          life: Math.random() * 1200,
          // Longer life = longer continuous ribbons before a particle
          // recycles. Webuzo's curls are dramatically long-lived; bump
          // this to mimic the long curling traces.
          maxLife: 1800 + Math.random() * 2400,
        });
      }
      particlesRef.current = list;
    };

    const resize = () => {
      const rect = wrap.getBoundingClientRect();
      width  = rect.width;
      height = rect.height;
      canvas.width  = Math.floor(width  * DPR);
      canvas.height = Math.floor(height * DPR);
      canvas.style.width  = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
      if (particlesRef.current.length === 0) seedParticles();
    };

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(wrap);

    // Window-level listener so the swirl reacts even when the cursor is
    // over sibling content (logo card, SOPs, etc.) layered on top of the
    // canvas. We translate window coords into canvas-local coords and
    // mark the cursor inactive when it leaves the wrapper bounds.
    const onPointerMove = (e) => {
      const rect = wrap.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const inside = x >= 0 && x <= rect.width && y >= 0 && y <= rect.height;
      mouseRef.current = { x, y, active: inside };
    };
    const onPointerLeaveWindow = () => {
      mouseRef.current.active = false;
    };

    window.addEventListener('mousemove', onPointerMove);
    window.addEventListener('mouseout', onPointerLeaveWindow);

    let lastT = performance.now();

    const tick = (now) => {
      const dt = Math.min(48, now - lastT) || 16;
      lastT = now;

      // Fade the previous frame slightly instead of wiping it. This is
      // what produces the "trail" effect — older line segments dim out
      // gradually rather than disappear instantly.
      ctx.fillStyle = `rgba(${trailColor}, ${trail})`;
      ctx.fillRect(0, 0, width, height);

      const m = mouseRef.current;
      const list = particlesRef.current;

      for (let i = 0; i < list.length; i++) {
        const p = list[i];

        // Ambient gentle drift so the field has motion even with no mouse.
        const t = now * 0.0003 + i * 0.13;
        p.vx += Math.cos(t) * 0.005;
        p.vy += Math.sin(t * 1.3) * 0.005;

        // Mouse swirl: tangential force perpendicular to (p - mouse),
        // plus a weak attractor so particles get caught in the orbit.
        if (m.active) {
          const dx = m.x - p.x;
          const dy = m.y - p.y;
          const d2 = dx * dx + dy * dy;
          const r  = swirlRadius;
          if (d2 < r * r && d2 > 1) {
            const d = Math.sqrt(d2);
            const falloff = 1 - d / r;
            const tangential = swirlStrength * falloff;
            const attract    = swirlStrength * 0.35 * falloff;
            // Tangential (perpendicular to radial)
            p.vx += (-dy / d) * tangential;
            p.vy += ( dx / d) * tangential;
            // Mild attraction
            p.vx += ( dx / d) * attract;
            p.vy += ( dy / d) * attract;
          }
        }

        // Velocity damping keeps the system stable. Higher damping value
        // (e.g. 0.992) lets ribbons keep their energy longer, which is
        // what gives the Webuzo-style long curls their character.
        p.vx *= damping;
        p.vy *= damping;

        const prevX = p.x;
        const prevY = p.y;
        p.x += p.vx * (dt / 16);
        p.y += p.vy * (dt / 16);

        // Wrap around edges so the canvas never empties.
        if (p.x < 0)      p.x += width;
        if (p.x > width)  p.x -= width;
        if (p.y < 0)      p.y += height;
        if (p.y > height) p.y -= height;

        // Draw the segment from previous to current position.
        // Skip when we wrapped (large jump) to avoid an ugly cross-canvas line.
        const dx2 = p.x - prevX;
        const dy2 = p.y - prevY;
        if (Math.abs(dx2) < width * 0.4 && Math.abs(dy2) < height * 0.4) {
          ctx.strokeStyle = p.color;
          ctx.globalAlpha = segmentAlpha;
          ctx.lineWidth = lineWidth;
          ctx.beginPath();
          ctx.moveTo(prevX, prevY);
          ctx.lineTo(p.x,   p.y);
          ctx.stroke();
        }

        p.life += 1;
        if (p.life > p.maxLife) {
          // Recycle so we don't see a particle suddenly vanish.
          p.x = Math.random() * width;
          p.y = Math.random() * height;
          p.vx = (Math.random() - 0.5) * 0.3;
          p.vy = (Math.random() - 0.5) * 0.3;
          p.life = 0;
          p.maxLife = 1800 + Math.random() * 2400;
          p.color = palette[Math.floor(Math.random() * palette.length)];
        }
      }

      ctx.globalAlpha = 1;
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
      window.removeEventListener('mousemove', onPointerMove);
      window.removeEventListener('mouseout', onPointerLeaveWindow);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={wrapperRef}
      style={{
        position: 'absolute', inset: 0, overflow: 'hidden',
        pointerEvents: 'none',                // listener is window-level; canvas
                                              // shouldn't block clicks on overlaid
                                              // brand content (SOP buttons, etc.)
        background,
      }}
    >
      <canvas
        ref={canvasRef}
        style={{ display: 'block', width: '100%', height: '100%' }}
      />
    </div>
  );
};

export default LoginSwirlBackground;
